import { dump, load } from "js-yaml";
import { isEqual } from "lodash";

// TODO write unit tests

export const parseYaml = (yaml) => {
    if (yaml == null || yaml === 0 || yaml === false) return yaml;
    if (isBlank(yaml)) return "";
    return load(yaml);
};

export const serializeToPrettyYaml = (value: any) => {
    if (value == null) return "null";
    if (value === 0 || value === false) return value.toString();
    if (typeof value === "string" && isBlank(value)) {
        return value.toString();
    }
    let yamlString = dump(value);

    // Remove the >- prefix if it exists
    if (yamlString.startsWith(">-")) {
        yamlString = yamlString.substring(2).trim();
    } else if (yamlString.startsWith(" >-")) {
        yamlString = yamlString.substring(3).trim();
    }
    return yamlString;
};

export const serializeToPrettyJson = (value: any) => {
    if (!value) return value;
    return JSON.stringify(value, null, 2);
};

const parseYamlSafely = (yaml: string) => {
    try {
        return parseYaml(yaml);
    } catch (error) {
        return null;
    }
};

export const isYamlEqual = (yamlA: string, yamlB: string) => {
    const aObj = parseYamlSafely(yamlA);
    const bObj = parseYamlSafely(yamlB);
    return isEqual(aObj, bObj);
};

export const isBlank = (string: string) => {
    return !string || /^\s*$/.test(string);
};

// Mode-aware (de)serialization for structured-data fields whose display can be
// toggled between YAML and JSON (see utils/structuredDataDisplayMode). The stored
// value is always a parsed object; the editor text is derived per mode.
export const serializeStructuredData = (value: any, mode: "yaml" | "json"): string => {
    if (mode === "json") {
        if (value == null) return "null";
        if (value === false) return "false";
        if (value === 0) return "0";
        if (typeof value === "string" && isBlank(value)) return value.toString();
        return JSON.stringify(value, null, 2);
    }
    return serializeToPrettyYaml(value);
};

export const parseStructuredData = (text: string, mode: "yaml" | "json") => {
    if (mode === "yaml") return parseYaml(text);
    // JSON mode: only JSON is accepted (strict). Mirror parseYaml's guards for
    // nullish/blank input so an empty editor does not throw on blur.
    if (text == null || (text as unknown) === 0 || (text as unknown) === false) return text;
    if (isBlank(text)) return "";
    return JSON.parse(text);
};

// Re-render an ALREADY-serialized structured-data string in the requested display
// mode. The canonical text a read-only field receives is pretty YAML (YAML is a
// superset of JSON, so it also parses text that happens to be JSON). We parse it
// back to an object and re-serialize in the target representation. On any parse
// failure the original text is returned unchanged so a non-structured or malformed
// value still displays as-is instead of vanishing. Used by CodeEditor for the
// read-only display toggle (structuredDataView); the edit path uses the
// object-based serializeStructuredData/parseStructuredData pair above.
export const convertStructuredTextForDisplay = (
    text: any,
    mode: "yaml" | "json"
): any => {
    if (typeof text !== "string" || isBlank(text)) return text;
    let parsed: any;
    try {
        parsed = parseYaml(text);
    } catch (error) {
        return text;
    }
    return serializeStructuredData(parsed, mode);
};

export const cleanAndNormalizeYaml = (yaml: string) => {
    // Remove unwanted YAML block indicators (|-, |+, etc.)
    const cleanedYaml = yaml.replace(/^\s*[\|-][\+-]?\s*$/gm, "");

    const parsedObject = parseYamlSafely(cleanedYaml);
    const normalizedYaml = serializeToPrettyYaml(parsedObject);

    return normalizedYaml;
};
